import React, { useState } from 'react';
import { FaSearch, FaFolder, FaEye } from 'react-icons/fa';
import { ExpandHeader, ToolCallWrapper } from '../shared/ToolCallComponents';
import Text from '../../Text/Text';
import { CollapsibleSection } from './shared/CollapsibleSection';

interface GrepToolProps {
    item: {
        id?: string;
        name?: string;
        args?: {
            input?: string;
            type?: string;
            filePath?: string;
            dirName?: string;
        };
        matches?: string[];
        fileContents?: string;
        error?: string;
    };
    isInProgress: boolean;
}

export const GrepCodebase: React.FC<GrepToolProps> = ({ item, isInProgress }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const fileCount = item.matches ? item.matches.length : 0;

    return (
        <ToolCallWrapper>
            <ExpandHeader
                icon={<FaSearch className="mr-2 text-neutral-500 dark:text-neutral-400" size={14} />}
                title={`Grepping codebase${fileCount > 0 ? ` (Listed ${fileCount} files)` : ''}`}
                isExpanded={isExpanded}
                isInProgress={isInProgress}
                onClick={() => setIsExpanded(prev => !prev)}
            />
            {isExpanded && (
                <div className="ml-2 mt-2">
                    {/* Display the search input and type in a styled box */}
                    {item.args?.input && (
                        <div className="mb-3 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-200 dark:border-neutral-800">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                                {item.args.type === 'regex' ? 'Regex Pattern' : 'Search String'}
                            </div>
                            <div className="font-medium">
                                {item.args.input}
                            </div>
                        </div>
                    )}

                    {/* Display matches */}
                    {item.matches && item.matches.length > 0 && (
                        <div className="space-y-2">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                                Found in {item.matches.length} file{item.matches.length !== 1 ? 's' : ''}
                            </div>
                            <div className="pl-2 space-y-2">
                                {item.matches.map((fileName: string, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center py-1 px-2 hover:bg-neutral-50 dark:hover:bg-neutral-900 rounded-md transition-colors duration-150"
                                    >
                                        <span className="text-neutral-400 dark:text-neutral-500 mr-2">•</span>
                                        <div className="hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-150">
                                            <Text item={{ text: `[${fileName}](${fileName})` }} index={0} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Show when no matches were found */}
                    {item.matches && item.matches.length === 0 && (
                        <div className="text-neutral-500 dark:text-neutral-400 italic">
                            No matching files found
                        </div>
                    )}
                </div>
            )}
        </ToolCallWrapper>
    );
};

export const OpenFile: React.FC<GrepToolProps> = ({ item, isInProgress }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <ToolCallWrapper>
            <ExpandHeader
                icon={<FaFolder className="mr-2 text-neutral-500 dark:text-neutral-400" size={14} />}
                title="Opening file"
                isExpanded={isExpanded}
                isInProgress={isInProgress}
                onClick={() => setIsExpanded(prev => !prev)}
            />
            {isExpanded && (
                <div className="ml-2 mt-2">
                    {item.args?.filePath && (
                        <div className="mb-3 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-200 dark:border-neutral-800">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                                File Path
                            </div>
                            <div className="font-medium">
                                <Text item={{ text: `[${item.args.filePath}](${item.args.filePath})` }} index={0} />
                            </div>
                        </div>
                    )}
                    {item.fileContents && (
                        <CollapsibleSection
                            title="File Contents"
                            collapseOnContent={false}
                            isLoading={isInProgress}
                            maxHeight={470}
                        >
                            <div className="bg-white dark:bg-neutral-800 p-4 rounded-lg shadow-sm">
                                <div className="text-neutral-700 dark:text-neutral-300 font-mono text-sm overflow-x-auto">
                                    <pre className="whitespace-pre-wrap">{item.fileContents}</pre>
                                </div>
                            </div>
                        </CollapsibleSection>
                    )}
                    {item.error && (
                        <div className="text-red-500 dark:text-red-400 italic mt-2">
                            {item.error}
                        </div>
                    )}
                </div>
            )}
        </ToolCallWrapper>
    );
};


export const ViewDirectory: React.FC<GrepToolProps> = ({ item, isInProgress }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <ToolCallWrapper>
            <ExpandHeader
                icon={<FaEye className="mr-2 text-neutral-500 dark:text-neutral-400" size={14} />}
                title="Viewing directory"
                isExpanded={isExpanded}
                isInProgress={isInProgress}
                onClick={() => setIsExpanded(prev => !prev)}
            />
            {isExpanded && (
                <div className="ml-2 mt-2">
                    {item.args?.dirName && (
                        <div className="mb-3 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-md border border-neutral-200 dark:border-neutral-800">
                            <div className="text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                                Directory Path
                            </div>
                            <div className="font-medium text-neutral-700 dark:text-neutral-300">
                                {item.args.dirName}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </ToolCallWrapper>
    );
};